import type { AboutPageData } from "$lib/types/about";
import { mediaUrl } from "$lib/utils/media";

export const aboutPageData: AboutPageData = {
  hero: {
    eyebrow: "About Studio Click House",
    headingLine1: "We Are",
    headingLine2: "Studio Click House",
    positioning:
      "A specialized post-production studio delivering high-end photo retouching, video color grading, and 3D CGI editing for global fashion labels, commercial e-commerce, and creative agencies.",
    stats: [
      {
        value: 150,
        suffix: "+",
        label: "Digital Artists",
        sublabel: "Retouchers, colorists & 3D artists",
      },
      {
        value: 2022,
        suffix: "",
        label: "Studio Founded",
        sublabel: "In Dhaka, Bangladesh",
      },
      {
        value: 24,
        suffix: "/7",
        label: "Production Coverage",
        sublabel: "Supporting work across time zones",
      },
    ],
    bannerMedia: {
      src: mediaUrl("/images/about/about-hero-production-floor-overview.webp"),
      alt: "Studio Click House main production floor with digital artists and calibrated workstations",
      width: 1152,
      height: 2048,
      credit: "Studio Click House production floor",
    },
  },
  people: {
    eyebrow: "Our People",
    heading: "The Team Behind Every Frame",
    subheading:
      "Our Dhaka studio brings together retouchers, colorists, 3D artists, and production leads who keep commercial work accurate and on schedule.",
    heroCollectiveMedia: {
      src: mediaUrl("/images/about/about-people-team-collective.webp"),
      alt: "Full Studio Click House post-production collective gathered at Dhaka studio workstations",
      width: 1600,
      height: 900,
      credit: "Studio Click House team collective",
    },
    moments: [
      {
        id: "production-house",
        title: "Production House",
        category: "Production Floor",
        media: {
          src: mediaUrl("/images/about/about-people-fashion-retouching-suite.webp"),
          alt: "Senior retouchers finishing fashion portrait editorial on calibrated display at Studio Click House",
          width: 1536,
          height: 2048,
          credit: "Production Suite",
        },
      },
      {
        id: "marketing-communications",
        title: "Marketing & Strategy",
        category: "Marketing & Communications",
        media: {
          src: mediaUrl("/images/about/about-people-client-communications-pod.webp"),
          alt: "Client communications and marketing team coordinating global brand campaigns and schedules at Studio Click House",
          width: 1200,
          height: 1600,
          credit: "Marketing Wing",
        },
      },
      {
        id: "admin-operations",
        title: "Daily AI Workflows",
        category: "AI & Production Team",
        media: {
          src: mediaUrl("/images/about/about-people-retouching-operations-floor.webp"),
          alt: "Admin and operations management team supervising floor workflows and shift capacity at Studio Click House",
          width: 1536,
          height: 2048,
          credit: "Operations Desk",
        },
      },
      {
        id: "quality-control",
        title: "Quality Control & Review",
        category: "Quality Assurance",
        media: {
          src: mediaUrl("/images/about/about-people-qa-studio-headquarters.webp"),
          alt: "Studio Click House QA workstation performing 100% zoom pixel check and pre-delivery inspection",
          width: 1536,
          height: 2048,
          credit: "QA Inspection Station",
        },
      },
    ],
        snapshots: [
      {
        id: "snapshot-studio-flow",
        title: "Daily Studio Flow",
        category: "Daily Rhythm",
        caption: "Collaborative workstations where retouchers and 3D artists shape commercial campaigns.",
        media: {
          src: mediaUrl("/images/about/about-snapshot-studio-flow.webp"),
          alt: "Daily studio workflow at Studio Click House Dhaka",
          width: 1600,
          height: 2000,
          credit: "Studio Floor",
        },
      },
      {
        id: "snapshot-collaboration",
        title: "Team Collaboration",
        category: "Teamwork & Craft",
        caption: "Peer review and shared feedback to ensure high visual standards across every project.",
        media: {
          src: mediaUrl("/images/about/about-snapshot-team-collaboration.webp"),
          alt: "Team collaboration and review at Studio Click House",
          width: 1200,
          height: 1500,
          credit: "Collaboration Suite",
        },
      },
      {
        id: "snapshot-client-support",
        title: "Global Client Support",
        category: "Client Support",
        caption: "Direct support channels and live communication keeping international projects on schedule.",
        media: {
          src: mediaUrl("/images/about/about-snapshot-cloud-pipeline-infrastructure.webp"),
          alt: "Client support specialist with headset coordinating projects at Studio Click House",
          width: 1536,
          height: 2048,
          credit: "Client Support Desk",
        },
      },
      {
        id: "snapshot-it-infrastructure",
        title: "IT & Server Infrastructure",
        category: "Enterprise Systems",
        caption: "High-speed local network, secure on-premise servers, and automated backups powering continuous operations.",
        media: {
          src: mediaUrl("/images/about/about-snapshot-it-server-infrastructure.webp"),
          alt: "IT specialist inspecting network server rack and data infrastructure at Studio Click House Dhaka",
          width: 1120,
          height: 1400,
          credit: "IT Server Suite",
        },
      },
      {
        id: "snapshot-quality-focus",
        title: "Style Guide Precision",
        category: "Attention to Detail",
        caption: "Adhering strictly to client brand presets, color profiles, and visual guidelines.",
        media: {
          src: mediaUrl("/images/about/about-snapshot-quality-precision.webp"),
          alt: "Client guideline verification and precision editing at Studio Click House",
          width: 1200,
          height: 1500,
          credit: "Precision Suite",
        },
      },
      {
        id: "snapshot-workspace-vibe",
        title: "Modern Workspace",
        category: "Studio Atmosphere",
        caption: "A clean, focused environment with natural light and calibrated workstations.",
        media: {
          src: mediaUrl("/images/about/about-snapshot-modern-workspace.webp"),
          alt: "Main workspace and studio environment at Studio Click House",
          width: 1600,
          height: 2000,
          credit: "Modern Workspace",
        },
      },
    ],
    snapshotsEyebrow: "Studio Culture & Craft",
    snapshotsHeading: "Inside Our Dhaka Headquarters",
  },
  values: {
    eyebrow: "What Drives Us",
    heading: "Core Values",
    subheading:
      "These six foundational principles guide every project, file transfer, and client partnership we touch.",
    items: [
      {
        number: "01",
        title: "Careful, consistent finishing",
        tagline: "Careful finishing at every scale",
        description:
          "From large e-commerce lookbooks to single hero images, we pay close attention to texture, lighting, and natural skin tones.",
      },
      {
        number: "02",
        title: "Speed with Scale",
        tagline: "Overnight turnaround with agreed delivery targets",
        description:
          "Our 24/7 dual-shift post-production model enables high-capacity throughput, offering global brands overnight turnarounds without sacrificing artistic control.",
      },
      {
        number: "03",
        title: "Transparent Partnership",
        tagline: "Direct line to your dedicated retouch team",
        description:
          "We act as an integrated extension of your creative team. Dedicated project managers and clear Slack/Teams lines ensure effortless communication.",
      },
      {
        number: "04",
        title: "Technological Innovation",
        tagline: "Blending human art direction with smart tooling",
        description:
          "We pioneer custom color management scripts, automated QC validation tools, and CGI hybrid pipelines to streamline turnaround and lower operational friction.",
      },
      {
        number: "05",
        title: "Data Security & Trust",
        tagline: "Enterprise-grade NDA & asset privacy",
        description:
          "Unreleased campaign media is protected by strict NDA protocols, encrypted R2 storage, watermarked internal proofs, and restricted air-gapped workstations.",
      },
      {
        number: "06",
        title: "Continuous Mastery",
        tagline: "Elevating craft through weekly masterclasses",
        description:
          "Post-production technology evolves constantly. We invest continuously in our artists' education across Unreal Engine 5, advanced compositing, and generative retouching.",
      },
    ],
  },
  journey: {
    eyebrow: "Our Journey",
    heading: "Our Story So Far",
    subheading:
      "From a modest 4-person photo editing cell in Dhaka to a 150+ specialist post-production force trusted by international fashion houses.",
    milestones: [
      {
        year: "2022",
        title: "The Studio Is Founded",
        subtitle: "Dhaka, Bangladesh",
        description:
          "Studio Click House started in a small workspace with four photo retouchers focused on fashion catalog editing.",
        statsHighlight: "4 Retouchers · 10 Clients",
        media: {
          src: mediaUrl("/images/about/about-snapshot-creative-studio-atmosphere.webp"),
          alt: "Early creative studio workspace at Studio Click House in Dhaka",
          width: 1536,
          height: 2048,
          credit: "Dhaka studio roots",
        },
      },
      {
        year: "2023",
        title: "Commercial E-Commerce Expansion",
        subtitle: "High-Volume Scaling",
        description:
          "Expanded our production floor to accommodate overnight bulk retouching for major European and North American fashion retailers.",
        statsHighlight: "35 Artists · 100k+ Images/Year",
        media: {
          src: mediaUrl("/images/about/about-timeline-2023-commercial-expansion.webp"),
          alt: "Commercial production floor expansion at Studio Click House",
          width: 1600,
          height: 2000,
          credit: "Commercial expansion floor",
        },
      },
      {
        year: "2024",
        title: "Video Editing & Motion Department",
        subtitle: "Multi-Media Production",
        description:
          "Launched our dedicated video post-production suite, offering broadcast color grading, video retouching, and commercial reel editing.",
        statsHighlight: "Video Suite · 10-Bit Color Workstations",
        media: {
          src: mediaUrl("/images/about/about-timeline-2024-video-suite.webp"),
          alt: "Production workstations and video post suite at Studio Click House",
          width: 1600,
          height: 2000,
          credit: "Video & retouching suite",
        },
      },
      {
        year: "2025",
        title: "3D & CGI Rendering Suite",
        subtitle: "Product Visualization",
        description:
          "Introduced 3D product modeling and CGI scene composition to serve jewelry, cosmetics, and footwear brands seeking virtual studio shoots.",
        statsHighlight: "3D Dept · Maya & Blender Pipeline",
        media: {
          src: mediaUrl("/videos/about-timeline-2025-cgi-rendering-suite.mp4"),
          alt: "3D and CGI product modeling video showcase at Studio Click House",
          width: 1600,
          height: 2000,
          credit: "3D & CGI Rendering Suite",
        },
      },
      {
        year: "2026",
        title: "Global Delivery Hub & Enterprise Infrastructure",
        subtitle: "Cloud Infrastructure",
        description:
          "Deployed high-speed private cloud pipeline with Cloudflare R2 storage, guaranteeing sub-hour delivery windows for time-critical press campaigns.",
        statsHighlight: "Cloudflare R2 · Global Delivery",
        media: {
          src: mediaUrl("/images/about/about-timeline-2026-client-success-verification.webp"),
          alt: "Global delivery and client communication pod at Studio Click House",
          width: 1200,
          height: 1600,
          credit: "Enterprise delivery operations",
        },
      },
      {
        year: "Present",
        title: "Next-Gen AI-Assisted Post Studio",
        subtitle: "Present & Beyond",
        description:
          "Pioneered proprietary workflow tooling integrating AI background synthesis with human master retouchers, doubling production velocity.",
        statsHighlight: "150+ Specialists · 24/7 Production",
        media: {
          src: mediaUrl("/images/about/about-timeline-present-ai-assisted-studio.webp"),
          alt: "Modern next-gen AI assisted retouching studio floor at Studio Click House",
          width: 1600,
          height: 2000,
          credit: "Next-gen AI production floor",
        },
      },
    ],
  },
  careers: {
    eyebrow: "Join Our Team",
    heading: "Your Next Chapter Starts Here",
    description:
      "We look for retouchers, video editors, 3D artists, and project leads who care about clear briefs, careful work, and reliable delivery.",
    ctaLabel: "View Open Positions",
    ctaHref: "/careers",
    bgMedia: {
      src: mediaUrl("/images/about/about-careers-workspace-culture.webp"),
      alt: "Creative discussion and workstation environment at Studio Click House headquarters",
      width: 1600,
      height: 2000,
      credit: "Studio culture photo",
    },
  },
  leadership: {
    eyebrow: "Leadership Team",
    heading: "Meet the Minds Guiding SCHL",
    subheading:
      "Our leadership combines deep technical post-production craft with international agency operational experience.",
    members: [
      {
        id: "leader-md-jilhaj",
        name: "Jilhaj Hossain",
        role: "Managing Director",
        bio: "Directs strategic growth, international client partnerships, and enterprise post-production operations for Studio Click House.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-leadership-md-jilhaj.webp"),
          alt: "Jilhaj Hossain - Managing Director",
          width: 848,
          height: 1264,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-nasir-uddin",
        name: "Nasir Uddin",
        role: "Director",
        bio: "Guides studio vision, organizational growth, and strategic leadership across all commercial retouching divisions.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-nasir-uddin.webp"),
          alt: "Nasir Uddin - Director",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-abbas-mandal",
        name: "Abbas Mandal",
        role: "Chief Marketing Officer",
        bio: "Leads global brand positioning, international outreach, and strategic market expansion for Studio Click House.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-abbas-mandal.webp"),
          alt: "Abbas Mandal - Chief Marketing Officer",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-saiful-islam-khan",
        name: "Saiful Islam Khan",
        role: "Head of Business Development & Operations",
        bio: "Drives client acquisition, strategic creative partnerships, and key enterprise account relations worldwide.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-saiful-islam-khan.webp"),
          alt: "Saiful Islam Khan - Head of Business Development & Operations",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-shahmiran-talukder",
        name: "Shahmiran Talukder",
        role: "Operations Manager",
        bio: "Oversees daily 24/7 dual-shift production pipelines, resource allocation, and high-throughput quality benchmarks.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-shahmiran-talukder.webp"),
          alt: "Shahmiran Talukder - Operations Manager",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-mohammad-raiyan-abrar",
        name: "Mohammad Raiyan Abrar",
        role: "Human Resources Manager",
        bio: "Fosters studio talent acquisition, artist well-being, team culture, and professional growth across all departments.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-mohammad-raiyan-abrar.webp"),
          alt: "Mohammad Raiyan Abrar - Human Resources Manager",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-yeasin-arafat",
        name: "Yeasin Arafat",
        role: "Administrative Manager",
        bio: "Coordinates administrative operations, facility management, and day-to-day studio resources.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-yeasin-arafat.webp"),
          alt: "Yeasin Arafat - Administrative Manager",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
      {
        id: "leader-rokibul-islam",
        name: "Rokibul Islam",
        role: "IT & Systems Specialist",
        bio: "Maintains network infrastructure, secure digital pipelines, workstations, and high-availability IT systems.",
        linkedinUrl: "https://linkedin.com",
        media: {
          src: mediaUrl("/images/about/about-team-rokibul-islam.webp"),
          alt: "Rokibul Islam - IT & Systems Specialist",
          width: 1120,
          height: 1400,
          credit: "Studio Click House Leadership",
        },
      },
    ],
  },
  closingCta: {
    heading: "Need a partner close to the work?",
    description:
      "Send a test image or video clip. We’ll confirm the workflow, turnaround, and delivery format before production starts.",
    primaryCtaLabel: "Start a Project",
    primaryCtaHref: "/contact",
    secondaryCtaLabel: "Explore Our Portfolio",
    secondaryCtaHref: "/portfolio",
  },
};
